import React from 'react'
import store from '../../state/store'
import _ from 'underscore'
import { Search } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import NavbarSearchResult from './search_result'

const RECENT_STORAGE_KEY = 'sales_searchbar_recent_results'
const RECENT_SEARCHES_STORAGE_KEY = 'sales_searchbar_recent_searches'
const MAX_RECENT = 7

class NavbarSearch extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      value: '',
      recentResults: [],
      recentSearchTerms: [],
      isFocused: false,
      highlightedIndex: -1,
      scrollOnHighlight: false,
      keyboardNavigating: false,
      // Hovering highlights a row too, but Enter only opens a row picked with the arrow keys.
      highlightFromKeyboard: false,
      searchWhatsappContent: false,
      searchEmailContent: false
    }

    this.onChange = this.onChange.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.inputRef = React.createRef()
    this.containerRef = React.createRef()
    this.itemsRef = React.createRef()
  }

  focus() {
    if (this.inputRef.current) {
      this.inputRef.current.focus()
      this.setState({ isFocused: true })
    }
  }

  componentDidMount() {
    this.lazyFetchResults = _.debounce(this.fetchResults, 500)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mousedown', this.handleClickOutside)
    this.setState({
      recentResults: this.loadFromStorage(RECENT_STORAGE_KEY),
      recentSearchTerms: this.loadFromStorage(RECENT_SEARCHES_STORAGE_KEY)
    })
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.highlightedIndex !== prevState.highlightedIndex && this.state.highlightedIndex >= 0 && this.state.scrollOnHighlight) {
      this.scrollIntoView()
      this.setState({ scrollOnHighlight: false })
    }
  }

  handleMouseMove() {
    if (this.state.keyboardNavigating) {
      this.setState({ keyboardNavigating: false })
    }
  }

  handleClickOutside(e) {
    if (this.state.isFocused && this.containerRef.current && !this.containerRef.current.contains(e.target)) {
      this.setState({ isFocused: false })
    }
  }

  loadFromStorage(key) {
    try {
      const raw = localStorage.getItem(key)
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  saveRecentResult(result) {
    try {
      const customerId = result.customer.id
      const filtered = this.state.recentResults.filter(r => r.customer.id !== customerId)
      const updated = [result, ...filtered].slice(0, MAX_RECENT)
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated))
      this.setState({ recentResults: updated })
    } catch {
      // localStorage unavailable (private mode, quota); silently skip
    }
  }

  saveRecentSearchTerm(term) {
    const cleaned = (term || '').trim()
    if (cleaned.length < 3) return
    try {
      const filtered = this.state.recentSearchTerms.filter(t => t.toLowerCase() !== cleaned.toLowerCase())
      const updated = [cleaned, ...filtered].slice(0, MAX_RECENT)
      localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(updated))
      this.setState({ recentSearchTerms: updated })
    } catch {
      // ignore
    }
  }

  clearRecent = (e) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      localStorage.removeItem(RECENT_STORAGE_KEY)
      localStorage.removeItem(RECENT_SEARCHES_STORAGE_KEY)
    } catch {
      // ignore
    }
    this.setState({ recentResults: [], recentSearchTerms: [], highlightedIndex: -1 })
  }

  handleRecentTermClick = (e, term) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ value: term, highlightedIndex: -1 })
    store.emit('search', term, this.contentOptions())
    if (this.inputRef.current) this.inputRef.current.focus()
  }

  contentOptions() {
    return {
      searchWhatsappContent: this.state.searchWhatsappContent,
      searchEmailContent: this.state.searchEmailContent
    }
  }

  // The results change, so the highlight is cleared rather than left on whoever lands in that row.
  onToggleWhatsappContent = (checked) => {
    this.setState({ searchWhatsappContent: !!checked, highlightedIndex: -1, highlightFromKeyboard: false }, this.refreshContentSearch)
  }

  onToggleEmailContent = (checked) => {
    this.setState({ searchEmailContent: !!checked, highlightedIndex: -1, highlightFromKeyboard: false }, this.refreshContentSearch)
  }

  // Re-runs the search with the new options and hands focus back to the input for keyboard navigation.
  refreshContentSearch = () => {
    this.fetchResults()
    this.inputRef.current.focus()
  }

  scrollIntoView() {
    const container = this.itemsRef.current
    if (!container) return
    const selected = container.children[this.state.highlightedIndex]
    if (selected) selected.scrollIntoView({ block: 'nearest' })
  }

  onFocus() {
    this.setState({ isFocused: true })
  }

  // Typing reopens the dropdown: after a result is opened the input keeps focus, so onFocus won't fire again.
  onChange() {
    this.setState({ value: this.inputRef.current.value, isFocused: true, highlightedIndex: -1, highlightFromKeyboard: false, scrollOnHighlight: false })
    this.lazyFetchResults()
  }

  fetchResults() {
    store.emit('search', this.inputRef.current.value, this.contentOptions())
  }

  navigableItems() {
    const value = this.state.value.trim()
    if (value.length >= 3) return this.props.search.results
    if (value.length === 0) return this.state.recentResults
    return []
  }

  handleResultClick = (result, resource) => {
    if (!resource) return

    this.saveRecentResult(result)
    this.saveRecentSearchTerm(this.state.value)

    if (resource.type == 'Customer') {
      window.open(resource.link)
    }
    else if (resource.type == 'Enquiry') {
      store.emit('enquiries:show', resource.id)
    }
    else if (resource.type == 'Lead') {
      store.emit('leads:show', resource.id)
    }

    this.setState({ value: '', isFocused: false, highlightedIndex: -1 })
  }

  onResultNameClick = (result) => {
    let resource = _.last(
      _.select(result.resources, function(resource) {
        return (resource.type != 'Customer')
      })
    )

    if (!resource) {
      resource = _.last(result.resources)
    }

    this.handleResultClick(result, resource)
  }

  onResourceClick = (result, resource) => {
    this.handleResultClick(result, resource)
  }

  onResultMouseEnter = (index) => {
    if (!this.state.keyboardNavigating) {
      this.setState({ highlightedIndex: index, highlightFromKeyboard: false, scrollOnHighlight: false })
    }
  }

  onKeyDown = (e) => {
    const { highlightedIndex, highlightFromKeyboard } = this.state
    const items = this.navigableItems()

    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.setState({
          highlightedIndex: Math.min(highlightedIndex + 1, items.length - 1),
          highlightFromKeyboard: true,
          scrollOnHighlight: true,
          keyboardNavigating: true
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        this.setState({
          highlightedIndex: Math.max(highlightedIndex - 1, -1),
          highlightFromKeyboard: true,
          scrollOnHighlight: true,
          keyboardNavigating: true
        })
        break
      case 'Enter':
        e.preventDefault()
        if (highlightFromKeyboard && highlightedIndex >= 0 && highlightedIndex < items.length) {
          this.onResultNameClick(items[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        this.setState({ highlightedIndex: -1, isFocused: false })
        this.inputRef.current.blur()
        break
    }
  }

  render() {
    const { value, recentResults, recentSearchTerms, isFocused, highlightedIndex, searchWhatsappContent, searchEmailContent } = this.state
    const results = this.props.search.results
    const loading = this.props.search.loading

    const trimmed = value.trim()
    const hasRecents = recentResults.length > 0
    const hasRecentSearches = recentSearchTerms.length > 0
    const showHelp = trimmed.length < 3
    const showRecentSearches = trimmed.length === 0 && hasRecentSearches
    const showRecents = trimmed.length === 0 && hasRecents
    const showResults = trimmed.length >= 3 && results.length > 0
    const showNoResults = trimmed.length >= 3 && !loading && results.length === 0
    const showClear = trimmed.length === 0 && (hasRecents || hasRecentSearches)

    return (
      <div ref={this.containerRef} className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search (⌘K)"
          ref={this.inputRef}
          value={value}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onKeyDown={this.onKeyDown}
          className="block w-[250px] h-9 rounded-full border border-input bg-background pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
          title="Press ⌘K (or Ctrl+K) to focus"
        />

        {isFocused && (
          <div className="absolute top-full left-0 mt-1 w-[650px] bg-popover text-popover-foreground border rounded-md shadow-md max-h-[60vh] overflow-y-auto z-50">
            {showHelp && (
              <div className="px-3.5 py-3 text-xs text-muted-foreground leading-relaxed border-b">
                Search by customer's name, email address or mobile number.
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3.5 py-2 border-b text-xs text-muted-foreground">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={searchWhatsappContent} onCheckedChange={this.onToggleWhatsappContent} />
                Search WhatsApp message text (slower)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={searchEmailContent} onCheckedChange={this.onToggleEmailContent} />
                Search email text (slower)
              </label>
            </div>

            {showClear && (
              <div className="flex items-center justify-between px-3.5 pt-3 pb-1">
                <span className="text-xs font-semibold text-foreground">History</span>
                <a
                  href="#"
                  onMouseDown={this.clearRecent}
                  className="text-[11px] text-muted-foreground opacity-60 no-underline hover:opacity-100 hover:underline"
                >
                  Clear
                </a>
              </div>
            )}

            {showRecentSearches && (
              <>
                <div className="px-3.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent searches
                </div>
                <div className="flex flex-wrap gap-1.5 px-3.5 pb-3">
                  {recentSearchTerms.map(term => (
                    <button
                      key={term}
                      type="button"
                      onMouseDown={(e) => this.handleRecentTermClick(e, term)}
                      className="inline-block px-2.5 py-0.5 text-[12px] text-foreground bg-muted rounded-full border-0 cursor-pointer hover:bg-accent"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </>
            )}

            {showRecents && (
              <>
                <div className="px-3.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent results
                </div>
                <div ref={this.itemsRef}>
                  {recentResults.map((result, index) => (
                    <NavbarSearchResult
                      key={`recent-${result.customer.id}`}
                      result={result}
                      onNameClick={this.onResultNameClick}
                      onResourceClick={this.onResourceClick}
                      isHighlighted={index === highlightedIndex}
                      onMouseEnter={() => this.onResultMouseEnter(index)}
                    />
                  ))}
                </div>
              </>
            )}

            {loading && (
              <div className="px-3.5 py-3 text-sm text-muted-foreground">Searching…</div>
            )}

            {showNoResults && (
              <div className="px-3.5 py-3 text-sm text-muted-foreground">
                No results for <span className="font-medium text-foreground">"{trimmed}"</span>.
              </div>
            )}

            {showResults && (
              <div ref={this.itemsRef}>
                {results.map((result, index) => (
                  <NavbarSearchResult
                    key={result.customer.id}
                    result={result}
                    onNameClick={this.onResultNameClick}
                    onResourceClick={this.onResourceClick}
                    isHighlighted={index === highlightedIndex}
                    onMouseEnter={() => this.onResultMouseEnter(index)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

export default NavbarSearch
